import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUrlParams } from '../../../hooks/useUrlParams';
import { loadOzonAttributes as loadOzonAttributesService, loadOzonAssortment as loadOzonAssortmentService, syncOzonData as syncOzonDataService, syncOzonFast as syncOzonFastService } from '../../../services/ozon';
import { getOzonAccounts, getOzonProjects, getOzonProjectCabinets } from '../../../utils/ozon-selectors';
import { copyTableWithStatus } from '../../../utils/clipboard';
import { getStickyClass, downloadCsv } from '../../../utils/table';
import logger from "../../../utils/logger";
const TEXT = {
  row_num: '№',
  offer: 'Артикул',
  image: 'Фото',
  name: 'Название',
  barcode: 'Штрихкод',
  store_name: 'Магазин',
  load_attributes_error: 'Не удалось загрузить атрибуты OZON.',
  load_assortment_error: 'Не удалось загрузить ассортимент OZON.'
};
const ASSORTMENT_HEADERS = [TEXT.row_num, TEXT.offer, TEXT.image, TEXT.name, 'Product ID', 'SKU', TEXT.barcode, TEXT.store_name];
export function useOzonState({
  request,
  showStatus,
  setStatus,
  token,
  activeSection,
  activeMarketplace,
  activeProjectId,
  setActiveProjectId,
  marketplaces,
  workspaces,
  openLightbox
}) {
  // Проект, кабинет, вкладка и подвкладка Ozon — параметры адреса (?project, ?account, ?tool, ?sub).
  // Состояния в памяти и пары эффектов «состояние ↔ адрес» убраны: F5, ссылка и «Назад» работают одинаково.
  const { get: getUrlParam, update: updateUrlParams } = useUrlParams();
  const ozonTab = getUrlParam('tool') || 'assortment';
  const ozonAccountId = getUrlParam('account') || null;
  const ozonProjectId = activeProjectId || getUrlParam('project') || null;
  const setOzonTab = useCallback((tab) => updateUrlParams({ tool: tab, sub: '' }, { push: true }), [updateUrlParams]);
  const [customerIdFilter, setCustomerIdFilter] = useState(() => new URLSearchParams(window.location.search).get('customerId') || '');
  const [ozonAttributes, setOzonAttributes] = useState({
    headers: [],
    rows: []
  });
  const [ozonAttributesTotal, setOzonAttributesTotal] = useState(0);
  const [ozonAssortment, setOzonAssortment] = useState([]);
  const [ozonAssortmentTotal, setOzonAssortmentTotal] = useState(0);
  const [ozonBusy, setOzonBusy] = useState(false);
  const ozonLoadingRef = useRef(false);
  const [assortmentPageSize, setAssortmentPageSize] = useState(50);
  const [assortmentPage, setAssortmentPage] = useState(1);
  const [attributesPageSize, setAttributesPageSize] = useState(50);
  const [attributesPage, setAttributesPage] = useState(1);
  const [assortmentSort, setAssortmentSort] = useState({
    index: 0,
    dir: 'asc'
  });
  const [attributesSort, setAttributesSort] = useState({
    index: 0,
    dir: 'asc'
  });
  const [assortmentSearch, setAssortmentSearch] = useState('');
  const [attributesSearch, setAttributesSearch] = useState('');
  const [attributesShowAllColumns, setAttributesShowAllColumns] = useState(false);
  const ATTRIBUTES_MAX_COLUMNS = 999;
  const ozonAccounts = useMemo(() => getOzonAccounts(marketplaces), [marketplaces]);
  const ozonProjects = useMemo(() => {
    const list = getOzonProjects(workspaces, ozonAccounts);
    // Проект, открытый явно (кнопка "Инструменты OZON" внутри проекта или
    // ?project=...), может не иметь привязанных Ozon-кабинетов и потому
    // отсутствовать в списке. Без этого он молча слетал на первый проект.
    if (ozonProjectId && !list.some(item => item.id === ozonProjectId)) {
      const selected = workspaces.find(item => item.id === ozonProjectId);
      if (selected) return [...list, selected];
    }
    return list;
  }, [workspaces, ozonAccounts, ozonProjectId]);
  const ozonProjectCabinets = useMemo(() => getOzonProjectCabinets(ozonProjectId, ozonProjects, ozonAccounts), [ozonProjectId, ozonProjects, ozonAccounts]);
  // Кабинет из адреса, которого нет у пользователя (чужая или устаревшая ссылка), сбрасываем — только когда
  // список кабинетов уже загружен, иначе корректная ссылка стёрлась бы до загрузки.
  useEffect(() => {
    if (ozonAccountId && ozonAccounts.length > 0 && !ozonAccounts.some(a => a.id === ozonAccountId)) {
      updateUrlParams({ account: '' });
    }
  }, [ozonAccountId, ozonAccounts]); // eslint-disable-line

  // Выбор проекта: меняет текущий проект приложения, сбрасывает кабинет и открывает аналитику проекта.
  const selectOzonProject = projectId => {
    updateUrlParams({ project: projectId, account: '', tool: 'analytics', sub: '' }, { push: true });
  };
  const selectOzonAccount = accountId => {
    // Из кабинета в кабинет остаёмся в том же инструменте; с уровня проекта открываем ассортимент.
    updateUrlParams(ozonAccountId && ozonTab !== 'analytics'
      ? { account: accountId }
      : { account: accountId, tool: 'assortment', sub: '' }, { push: true });
  };
  const assortmentTotalPages = Math.max(1, Math.ceil(ozonAssortmentTotal / assortmentPageSize));
  const assortmentPageSafe = Math.min(assortmentPage, assortmentTotalPages);
  const assortmentSliceStart = (assortmentPageSafe - 1) * assortmentPageSize;
  const assortmentSlice = ozonAssortment;
  const attributesTotalPages = Math.max(1, Math.ceil(ozonAttributesTotal / attributesPageSize));
  const attributesPageSafe = Math.min(attributesPage, attributesTotalPages);
  const attributesSliceStart = (attributesPageSafe - 1) * attributesPageSize;
  const attributesSlice = ozonAttributes.rows;
  const attributesSliceEnd = attributesSliceStart + attributesPageSize;
  const attributesOfferIndex = ozonAttributes.headers.indexOf(TEXT.offer);
  const attributesHeadersFull = useMemo(() => {
    const headers = ozonAttributes.headers || [];
    const withoutOffer = headers.filter((_, idx) => idx !== attributesOfferIndex);
    return [TEXT.row_num, TEXT.offer, ...withoutOffer];
  }, [ozonAttributes.headers, attributesOfferIndex]);
  const attributesHeadersDisplay = useMemo(() => {
    if (attributesShowAllColumns || attributesHeadersFull.length <= ATTRIBUTES_MAX_COLUMNS) {
      return attributesHeadersFull;
    }
    return attributesHeadersFull.slice(0, ATTRIBUTES_MAX_COLUMNS);
  }, [attributesHeadersFull, attributesShowAllColumns, ATTRIBUTES_MAX_COLUMNS]);
  const attributesRowsDisplay = useMemo(() => {
    const maxCols = attributesShowAllColumns ? Infinity : ATTRIBUTES_MAX_COLUMNS;
    return attributesSlice.map((row, index) => {
      const offerValue = attributesOfferIndex >= 0 ? row[attributesOfferIndex] : '';
      const withoutOffer = row.filter((_, idx) => idx !== attributesOfferIndex);
      const fullRow = [attributesSliceStart + index + 1, offerValue, ...withoutOffer];
      return fullRow.slice(0, maxCols);
    });
  }, [attributesSlice, attributesOfferIndex, attributesSliceStart, attributesShowAllColumns, ATTRIBUTES_MAX_COLUMNS]);
  const assortmentHeadersDisplay = useMemo(() => ASSORTMENT_HEADERS, []);
  const assortmentRowsDisplay = useMemo(() => {
    return assortmentSlice.map((item, index) => [assortmentSliceStart + index + 1, item.offer_id || '', item.image_url || '', item.name || '', item.product_id || '', item.sku || '', item.barcode || '', item.store_name || '']);
  }, [assortmentSlice, assortmentSliceStart]);
  const assortmentRowsView = assortmentRowsDisplay;
  const attributesRowsView = attributesRowsDisplay;
  const loadOzonAttributesLocal = async (accountId, limitValue, offsetValue) => loadOzonAttributesService({
    request,
    accountId,
    limit: limitValue,
    offset: offsetValue,
    attributesHeadersDisplay,
    attributesSort,
    attributesSearch,
    setOzonAttributes,
    setOzonAttributesTotal
  });
  const loadOzonAssortmentLocal = async (accountId, limitValue, offsetValue) => loadOzonAssortmentService({
    request,
    accountId,
    limit: limitValue,
    offset: offsetValue,
    assortmentHeadersDisplay,
    assortmentSort,
    assortmentSearch,
    setOzonAssortment,
    setOzonAssortmentTotal
  });
  const syncOzonData = async () => syncOzonDataService({
    request,
    ozonAccountId,
    setOzonBusy,
    setStatus,
    showStatus,
    loadOzonAttributes: loadOzonAttributesLocal,
    loadOzonAssortment: loadOzonAssortmentLocal,
    attributesPageSize,
    attributesSliceStart,
    assortmentPageSize,
    assortmentSliceStart
  });
  const syncOzonFast = async () => syncOzonFastService({
    request,
    ozonAccountId,
    setOzonBusy,
    setStatus,
    showStatus,
    loadOzonAttributes: loadOzonAttributesLocal,
    loadOzonAssortment: loadOzonAssortmentLocal,
    attributesPageSize,
    attributesSliceStart,
    assortmentPageSize,
    assortmentSliceStart
  });
  // Переключает вкладку в памяти, без записи в адресную строку: данные "Повторных
  // заказов" не персистентны сами по себе (нужна ручная синхронизация отчёта), поэтому
  // постоянная ссылка с customerId в URL вводит в заблуждение — выглядит как то, что можно
  // сохранить/переслать и получить те же данные, а по факту это не гарантировано.
  const openOzonCustomer = (customerId) => {
    setCustomerIdFilter(customerId);
    setOzonTab('repeat-orders');
  };
  // Bumped by refreshOzonList() — included in requestKey below so a click on the
  // "Обновить" button in the Assortment/Attributes tables re-runs the fetch even
  // when nothing else (page/search/sort) changed.
  const [ozonListRefreshNonce, setOzonListRefreshNonce] = useState(0);
  const refreshOzonList = () => setOzonListRefreshNonce(n => n + 1);
  useEffect(() => {
    if (!token || !ozonAccountId) return;
    if (activeSection === 'marketplace' && activeMarketplace === 'ozon') {
      const attrOffset = (attributesPage - 1) * attributesPageSize;
      const assortOffset = (assortmentPage - 1) * assortmentPageSize;
      const requestKey = `${ozonAccountId}-${ozonTab}-${attrOffset}-${assortOffset}-${attributesSearch}-${assortmentSearch}-${attributesSort.index}-${attributesSort.dir}-${assortmentSort.index}-${assortmentSort.dir}-${ozonListRefreshNonce}`;
      if (ozonLoadingRef.current === requestKey) return;
      if (ozonTab === 'attributes') {
        ozonLoadingRef.current = requestKey;
        loadOzonAttributesLocal(ozonAccountId, attributesPageSize, attrOffset).catch(error => {
          logger.error(error);
          showStatus('error', TEXT.load_attributes_error);
        }).finally(() => {
          if (ozonLoadingRef.current === requestKey) {
            ozonLoadingRef.current = false;
          }
        });
      }
      if (ozonTab === 'assortment') {
        ozonLoadingRef.current = requestKey;
        loadOzonAssortmentLocal(ozonAccountId, assortmentPageSize, assortOffset).catch(error => {
          logger.error(error);
          showStatus('error', TEXT.load_assortment_error);
        }).finally(() => {
          if (ozonLoadingRef.current === requestKey) {
            ozonLoadingRef.current = false;
          }
        });
      }
    }
  }, [token, activeSection, activeMarketplace, ozonTab, ozonAccountId, assortmentPageSize, assortmentPage, attributesPageSize, attributesPage, assortmentSearch, attributesSearch, assortmentSort.index, assortmentSort.dir, attributesSort.index, attributesSort.dir]);
  useEffect(() => {
    setAssortmentPage(1);
  }, [assortmentPageSize, assortmentSearch, assortmentSort.index, assortmentSort.dir, ozonAccountId]);
  useEffect(() => {
    setAttributesPage(1);
  }, [attributesPageSize, attributesSearch, attributesSort.index, attributesSort.dir, ozonAccountId]);
  const copyTable = (headers, rows, label) => copyTableWithStatus(headers, rows, label, showStatus);
  const ozonSectionProps = {
    ozonBusy,
    ozonAccounts,
    ozonProjects,
    ozonProjectId,
    ozonProjectCabinets,
    ozonAccountId,
    ozonTab,
    setOzonTab,
    customerIdFilter,
    openOzonCustomer,
    selectOzonProject,
    selectOzonAccount,
    openLightbox,
    syncOzonFast,
    syncOzonData,
    assortmentPageSafe,
    assortmentTotalPages,
    assortmentPageSize,
    setAssortmentPage,
    setAssortmentPageSize,
    assortmentSearch,
    setAssortmentSearch,
    copyTable,
    downloadCsv,
    ozonAssortment,
    assortmentSliceStart,
    assortmentHeadersDisplay,
    getStickyClass,
    assortmentSort,
    setAssortmentSort,
    assortmentRowsView,
    attributesPageSafe,
    attributesTotalPages,
    attributesPageSize,
    setAttributesPage,
    setAttributesPageSize,
    attributesSearch,
    setAttributesSearch,
    ozonAttributes,
    attributesHeadersFull,
    attributesRowsView,
    attributesSliceStart,
    attributesSliceEnd,
    attributesSort,
    setAttributesSort,
    ATTRIBUTES_MAX_COLUMNS,
    refreshOzonList
  };
  return {
    ozonSectionProps
  };
}
